import { AnimatePresence, motion } from "framer-motion";
import {
  ChartColumnDecreasing,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  LayoutDashboard,
  LogOut,
  Settings,
  UserPlus,
  Users,
  Zap,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { BsFillCreditCard2FrontFill } from "react-icons/bs";
import { CgProfile } from "react-icons/cg";
import { FaClipboardList, FaRegCreditCard, FaUserFriends } from "react-icons/fa";
import { FaList } from "react-icons/fa6";
import { ImProfile } from "react-icons/im";
import { IoIosPersonAdd } from "react-icons/io";
import { MdHelpOutline, MdMarkEmailRead } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import LogoutLogo from "../assets/logout.png";
import logo from "../assets/SelfSync Logo.png";
import { getUserDetails, logoutUser } from "../redux/ReduxSlices/authSlice";
import { CiMail } from "react-icons/ci";

const Sidebar = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [signOutPopup, setSignOutPopup] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { userData } = useSelector((state) => state.auth);

  const userType = localStorage.getItem("userType");
  const userId = localStorage.getItem("user_id");
  const userInitial =
    localStorage.getItem("userName")?.charAt(0).toUpperCase() || "U";

  const [showHamburger, setShowHamburger] = useState(true);
  const scrollContainerRef = useRef(null);
  const scrollYRef = useRef(0);
  const rafRef = useRef(null);
  const scrollStopTimer = useRef(null);

  // Load active dropdown from localStorage on component mount
  useEffect(() => {
    const savedDropdown = localStorage.getItem("activeDropdown");
    if (savedDropdown) {
      setActiveDropdown(savedDropdown);
    }
  }, []);

  // Save active dropdown to localStorage whenever it changes
  useEffect(() => {
    if (activeDropdown) {
      localStorage.setItem("activeDropdown", activeDropdown);
    } else {
      localStorage.removeItem("activeDropdown");
    }
  }, [activeDropdown]);

  useEffect(() => {
    const container = document.getElementById("content-scroll");
    if (!container) return;

    scrollContainerRef.current = container;
    scrollYRef.current = container.scrollTop;

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);

      rafRef.current = requestAnimationFrame(() => {
        const currentY = container.scrollTop;

        // Scroll down = hide hamburger
        if (currentY > scrollYRef.current + 5) {
          setShowHamburger(false);
        }
        // Scroll up = show hamburger
        else if (currentY < scrollYRef.current - 5) {
          setShowHamburger(true);
        }

        scrollYRef.current = currentY;

        // Also show hamburger after scrolling stops
        if (scrollStopTimer.current) clearTimeout(scrollStopTimer.current);
        scrollStopTimer.current = setTimeout(() => {
          setShowHamburger(true);
        }, 500);
      });
    };

    container.addEventListener("scroll", onScroll);

    return () => {
      container.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (scrollStopTimer.current) clearTimeout(scrollStopTimer.current);
    };
  }, []);

  useEffect(() => {
    dispatch(getUserDetails());
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logoutUser());
  };

  const toggleDropdown = (dropdownName) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  useEffect(() => {
    if ((location.pathname === "/hire-through-vendor" || location.pathname === "/view-open-position") && activeDropdown !== "vendors") {
      toggleDropdown('vendors')
    } else if ((location.pathname === "/settings" || location.pathname === "/get-free-credits" || location.pathname === "/email-template") && activeDropdown !== "settings") {
      toggleDropdown('settings')
    }

  }, [location])

  // Define role-based navigation items
  const navItems = {
    owner: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} />, tooltip: "sidebarDashboardButtonTooltip" },
      {
        label: "Assessments",
        path: "/assessments",
        icon: <ClipboardList size={18} />,
        tooltip: "sidebarAssessmentsButtonTooltip"
      },
      {
        label: "Assessment Results",
        path: "/report",
        icon: <ChartColumnDecreasing size={18} />,
        tooltip: "sidebarResultButtonTooltip"
      },
      {
        label: "Create Positions",
        path: "/open-positions",
        icon: <ImProfile size={18} />,
        tooltip: "sidebarCreatePositionButtonTooltip"
      },
      {
        label: "Team Management",
        path: "/add-admin",
        icon: <Users size={18} />,
        tooltip: "sidebarTeamManagementButtonTooltip"
      },
    ],
    hr: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} />, tooltip: "sidebarDashboardButtonTooltip" },
      {
        label: "Assessments",
        path: "/assessments",
        icon: <ClipboardList size={18} />,
        tooltip: "sidebarAssessmentsButtonTooltip"
      },
      {
        label: "Assessment Results",
        path: "/report",
        icon: <ChartColumnDecreasing size={18} />,
        tooltip: "sidebarResultButtonTooltip"
      },
      {
        label: "Create Positions",
        path: "/open-positions",
        icon: <ImProfile size={18} />,
        tooltip: "sidebarCreatePositionButtonTooltip"
      },
      {
        label: "Team Management",
        path: "/add-admin",
        icon: <Users size={18} />,
        tooltip: "sidebarTeamManagementButtonTooltip"
      },
    ],
    manager: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} />, tooltip: "sidebarDashboardButtonTooltip" },
      {
        label: "Assessments",
        path: "/assessments",
        icon: <ClipboardList size={18} />,
        tooltip: "sidebarAssessmentsButtonTooltip"
      },
      {
        label: "Assessment Results",
        path: "/report",
        icon: <ChartColumnDecreasing size={18} />,
        tooltip: "sidebarResultButtonTooltip"
      },
    ],
    executive: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} />, tooltip: "sidebarDashboardButtonTooltip" },
      {
        label: "Assessment Results",
        path: "/report",
        icon: <ChartColumnDecreasing size={18} />,
        tooltip: "sidebarResultButtonTooltip"
      },
    ],
    individual: [
      { label: "Dashboard", path: "/", icon: <LayoutDashboard size={18} />, tooltip: "sidebarDashboardButtonTooltip" },
      {
        label: "Assessments",
        path: "/individual-assessment",
        icon: <ClipboardList size={18} />,
        tooltip: ""
      },
      {
        label: "Assessment Results",
        path: "/student-report",
        icon: <ChartColumnDecreasing size={18} />,
        tooltip: ""
      },
    ]
  };

  // if (userId === "7f5eb509-4ee7-4c3c-a03b-46385666ad2b") {
  //   navItems.owner.push(
  //     {
  //       label: "Waiting List",
  //       path: "/seo-data",
  //       icon: <FaClipboardList size={18} />,
  //     },
  //     {
  //       label: "Partner Lead",
  //       path: "/partner-lead",
  //       icon: <FaUserFriends size={18} />,
  //     }
  //   );
  // }

  // Merge roles: treat CFO as owner
  let mergedUserType = userType;
  if (userType === "cfo") {
    mergedUserType = "owner";
  }

  const currentNavItems = navItems[mergedUserType] || [];

  // Filter by user-specific visibility if needed
  const menu = currentNavItems.filter(
    (item) => !item.visibleTo || item.visibleTo.includes(userId)
  );

  // Define which roles should see "Upgrade Plan"
  const payNavItems = {
    hr: "no",
    cfo: "Yes",
    owner: "Yes",
    manager: "No",
    executive: "No",
    individual: "Yes"
  };

  const paymentNavItem = payNavItems[userType] || "No";

  const handleNavigateHelp = () => {
    setTimeout(() => setIsOpen(false), 100);
    if (userType === "owner") {
      navigate("/helpinfo");
    } else if (userType === "individual") {
      navigate("/helpinfo-individual");
    }
  }

  return (
    <div className="flex ">
      {/* Mobile Backdrop Overlay */}
      {isOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/30 backdrop-blur-sm z-30"
          onClick={() => setIsOpen(false)}
        />
      )}

      <div
        className={`bg-white w-64 h-screen fixed lg:static shadow-xl shadow-slate-300 flex flex-col justify-between transition-transform duration-300 z-40 overflow-y-auto ${isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
          }`}
      >
        {/* Logo */}
        <div>
          <motion.div className="flex items-center px-6 py-4 max-md:justify-center"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <img src={logo} alt="Logo" className="w-30 h-10 object-fit" />
          </motion.div>

          {/* Navigation */}
          <motion.nav className="px-4 py-2 overflow-y-auto"
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <ul className="space-y-2 text-sm font-medium cursor-pointer">
              {menu.map((item) => (
                <SidebarItem
                  key={item.path}
                  icon={item.icon}
                  label={item.label}
                  to={item.path}
                  setIsOpen={setIsOpen}
                  tooltip={item.tooltip}
                />
              ))}

              {/* Team Management Dropdown (for owner and hr) */}
              {/* {(userType === "owner" || userType === "hr" || userType === "cfo") && (
                <li>
                  <motion.div
                    className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer ${activeDropdown === 'team' ? "font-semibold bg-violet-100 text-violet-600" : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"}`}
                    onClick={() => toggleDropdown('team')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center">
                      <motion.div
                        animate={{ rotate: activeDropdown === 'team' ? 10 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Users size={18} className="mr-3" />
                      </motion.div>
                      Team Management
                    </div>
                    <motion.div
                      animate={{ rotate: activeDropdown === 'team' ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {activeDropdown === 'team' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </motion.div>
                  </motion.div>

                  <AnimatePresence>
                    {activeDropdown === 'team' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                          height: "auto",
                          opacity: 1,
                          transition: {
                            height: { duration: 0.3 },
                            opacity: { duration: 0.2, delay: 0.1 }
                          }
                        }}
                        exit={{
                          height: 0,
                          opacity: 0,
                          transition: {
                            height: { duration: 0.3 },
                            opacity: { duration: 0.1 }
                          }
                        }}
                        className="overflow-hidden"
                      >
                        <motion.div
                          className="ml-8 mt-1 space-y-1"
                          initial={{ x: -10 }}
                          animate={{ x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          {(userType === "owner" || userType === "cfo") && (
                            <motion.div
                              whileHover={{ x: 5 }}
                              transition={{ type: "spring", stiffness: 300 }}
                            >
                              <NavLink
                                to="/add-admin"
                                className={({ isActive }) =>
                                  `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm ${isActive
                                    ? "font-semibold bg-violet-50 text-violet-600"
                                    : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                                  }`
                                }
                                onClick={() => {
                                  setTimeout(() => setIsOpen(false), 100);
                                }}
                              >
                                <motion.span
                                  className="mr-3"
                                  animate={{ scale: [1, 1.2, 1] }}
                                  transition={{ duration: 0.5 }}
                                >
                                  <UserPlus size={16} />
                                </motion.span>
                                {userType === "cfo" ? "Add Site Manager" : "Add Admin Users"}
                              </NavLink>
                            </motion.div>
                          )}

                          <motion.div
                            whileHover={{ x: 5 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <NavLink
                              to="/assign-role"
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm ${isActive
                                  ? "font-semibold bg-violet-50 text-violet-600"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                                }`
                              }
                              onClick={() => {
                                setTimeout(() => setIsOpen(false), 100);
                              }}
                            >
                              <motion.span
                                className="mr-3"
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ duration: 0.7 }}
                              >
                                <UserPlus size={16} />
                              </motion.span>
                              Add Reporting Managers
                            </NavLink>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              )} */}

              {/* Vendors Dropdown */}
              {/* {(userType === "owner" || userType === "hr") && (
                <li>
                  <motion.div
                    className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer ${activeDropdown === 'vendors' ? "font-semibold bg-violet-100 text-violet-600" : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"}`}
                    onClick={() => toggleDropdown('vendors')}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <div className="flex items-center">
                      <motion.div
                        animate={{ rotate: activeDropdown === 'vendors' ? 10 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <Users size={18} className="mr-3" />
                      </motion.div>
                      Vendors
                    </div>
                    <motion.div
                      animate={{ rotate: activeDropdown === 'vendors' ? 180 : 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {activeDropdown === 'vendors' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </motion.div>
                  </motion.div>

                  <AnimatePresence>
                    {activeDropdown === 'vendors' && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{
                          height: "auto",
                          opacity: 1,
                          transition: {
                            height: { duration: 0.3 },
                            opacity: { duration: 0.2, delay: 0.1 }
                          }
                        }}
                        exit={{
                          height: 0,
                          opacity: 0,
                          transition: {
                            height: { duration: 0.3 },
                            opacity: { duration: 0.1 }
                          }
                        }}
                        className="overflow-hidden"
                      >
                        <motion.div
                          className="ml-8 mt-1 space-y-1"
                          initial={{ x: -10 }}
                          animate={{ x: 0 }}
                          transition={{ delay: 0.2 }}
                        >
                          <motion.div
                            whileHover={{ x: 5 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <NavLink
                              to="/hire-through-vendor"
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm ${isActive
                                  ? "font-semibold bg-violet-50 text-violet-600"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                                }`
                              }
                              onClick={() => {
                                setTimeout(() => setIsOpen(false), 100);
                              }}
                            >
                              <motion.span
                                className="mr-3"
                                animate={{ scale: [1, 1.2, 1] }}
                                transition={{ duration: 0.5 }}
                              >
                                <IoIosPersonAdd size={16} />
                              </motion.span>
                              Hire Through Vendor
                            </NavLink>
                          </motion.div>

                          <motion.div
                            whileHover={{ x: 5 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          >
                            <NavLink
                              to="/view-open-position"
                              className={({ isActive }) =>
                                `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm ${isActive
                                  ? "font-semibold bg-violet-50 text-violet-600"
                                  : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                                }`
                              }
                              onClick={() => {
                                setTimeout(() => setIsOpen(false), 100);
                              }}
                            >
                              <motion.span
                                className="mr-3"
                                animate={{ rotate: [0, 5, -5, 0] }}
                                transition={{ duration: 0.7 }}
                              >
                                <FaList size={16} />
                              </motion.span>
                              View Shared Positions
                            </NavLink>
                          </motion.div>
                        </motion.div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </li>
              )} */}

              {/* Settings Dropdown */}
              <li>
                <motion.div
                  className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer ${activeDropdown === 'settings' ? "font-semibold bg-violet-100 text-violet-600" : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"}`}
                  onClick={() => toggleDropdown('settings')}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="flex items-center">
                    <motion.div
                      animate={{ rotate: activeDropdown === 'settings' ? 10 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Settings size={18} className="mr-3" />
                    </motion.div>
                    Settings
                  </div>
                  <motion.div
                    animate={{ rotate: activeDropdown === 'settings' ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    {activeDropdown === 'settings' ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                  </motion.div>
                </motion.div>

                <AnimatePresence>
                  {activeDropdown === 'settings' && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                        transition: {
                          height: { duration: 0.3 },
                          opacity: { duration: 0.2, delay: 0.1 }
                        }
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                        transition: {
                          height: { duration: 0.3 },
                          opacity: { duration: 0.1 }
                        }
                      }}
                      className="overflow-hidden"
                    >
                      <motion.div
                        className="ml-8 mt-1 space-y-1"
                        initial={{ x: -10 }}
                        animate={{ x: 0 }}
                        transition={{ delay: 0.2 }}
                      >
                        <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <NavLink
                            to="/settings"
                            className={({ isActive }) =>
                              `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm sidebarProfileButtonTooltip ${isActive
                                ? "font-semibold bg-violet-50 text-violet-600"
                                : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                              }`
                            }
                            onClick={() => {
                              setTimeout(() => setIsOpen(false), 100);
                            }}
                          >
                            <motion.span
                              className="mr-3"
                              animate={{ scale: [1, 1.2, 1] }}
                              transition={{ duration: 0.5 }}
                            >
                              <CgProfile size={18} />
                            </motion.span>
                            Profile
                          </NavLink>
                        </motion.div>

                        <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <NavLink
                            to="/get-free-credits"
                            className={({ isActive }) =>
                              `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm sidebarFreeCreditButtonTooltip ${isActive
                                ? "font-semibold bg-violet-50 text-violet-600"
                                : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                              }`
                            }
                            onClick={() => {
                              setTimeout(() => setIsOpen(false), 100);
                            }}
                          >
                            <motion.span
                              className="mr-3"
                              animate={{ rotate: [0, 10, -10, 0] }}
                              transition={{ duration: 0.7 }}
                            >
                              <BsFillCreditCard2FrontFill size={16} />
                            </motion.span>
                            Get Free Credits
                          </NavLink>
                        </motion.div>

                        {userType !== "individual" && <motion.div
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 300 }}
                        >
                          <NavLink
                            to="/email-template"
                            className={({ isActive }) =>
                              `flex items-center px-3 py-2 rounded-md cursor-pointer text-sm sidebarTemplatesButtonTooltip ${isActive
                                ? "font-semibold bg-violet-50 text-violet-600"
                                : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
                              }`
                            }
                            onClick={() => {
                              setTimeout(() => setIsOpen(false), 100);
                            }}
                          >
                            <motion.span
                              className="mr-3"
                              animate={{ scale: [1, 1.1, 1] }}
                              transition={{ duration: 0.5, repeat: Infinity, repeatDelay: 2 }}
                            >
                              <MdMarkEmailRead size={16} />
                            </motion.span>
                            Email Template
                          </NavLink>
                        </motion.div>}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </li>
            </ul>
          </motion.nav>
        </div>

        {/* Footer */}
        <motion.div className="px-4 py-4 space-y-2 mb-32 md:mb-0"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <button
            className="flex items-center text-violet-700 bg-violet-50 px-3 py-2 w-full rounded-md hover:bg-violet-100 text-sm cursor-pointer sidebarContactUsButtonTooltip"
          // onClick={handleNavigateHelp}
          >
            <a
              href="https://selfsync.ai/contact-us"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center"
            >
              <CiMail className="mr-2 h-4 w-4" />
              Contact Us
            </a>
          </button>
          <button
            className="flex items-center text-violet-700 bg-violet-50 px-3 py-2 w-full rounded-md hover:bg-violet-100 text-sm cursor-pointer sidebarDocumentationButtonTooltip"
            onClick={handleNavigateHelp}
          >
            <MdHelpOutline className="mr-2 h-4 w-4" />
            Documentation
          </button>
          <div className="flex items-center justify-between text-violet-700 bg-gray-50 border px-3 py-2 w-full rounded-md text-sm sidebarCreditButtonTooltip">
            <div className="flex items-center">
              <FaRegCreditCard className="mr-2 h-4 w-4" />
              {userData.company_info?.credit_point ?? 0} Credits
            </div>
            <div className="ml-4 w-6 h-6 rounded-full bg-violet-200 text-violet-800 flex items-center justify-center text-xs font-semibold">
              {userInitial}
            </div>
          </div>

          {paymentNavItem === "Yes" && (
            <button
              className="flex items-center text-violet-700 bg-violet-50 px-3 py-2 w-full rounded-md hover:bg-violet-100 text-sm cursor-pointer sidebarPlansButtonTooltip"
              onClick={() => {
                setIsOpen(false);
                setTimeout(() => {
                  navigate("/pricing");
                }, 100);
              }}
            >
              <Zap className="mr-2 h-4 w-4" />
              Upgrade Plan
            </button>
          )}

          <button
            className="flex items-center text-gray-600 hover:text-red-500 px-3 py-2 w-full rounded-md text-sm"
            onClick={() => setSignOutPopup(true)}
          >
            <LogOut className="mr-2 h-4 w-4" />
            Sign Out
          </button>
        </motion.div>
      </div>

      {/* Mobile Hamburger */}
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`lg:hidden fixed top-4 left-4 z-50 bg-white border rounded-md p-2 shadow transition-all duration-300 ease-in-out transform ${!isOpen && showHamburger ? "translate-x-0 opacity-100" : "-translate-x-24 opacity-0"
          }`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="text-gray-700 text-xl">☰</span>
      </motion.button>

      {/* Logout Confirmation Modal */}
      {signOutPopup && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-70 p-4 z-50">
          <div className="relative bg-gradient-to-br from-white to-violet-50 p-6 rounded-xl shadow-2xl w-[380px] space-y-5 text-center">
            <div className="flex justify-center">
              <div className="bg-violet-100 p-4 rounded-full">
                <img
                  src={LogoutLogo}
                  alt="Logout Icon"
                  className="w-10 h-10 object-contain"
                />
              </div>
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Are you sure you want to sign out?
            </h2>
            <div className="flex justify-evenly gap-4">
              <button
                className="btn-gradient text-white py-2 px-8 rounded-md  transition-all"
                onClick={handleLogout}
              >
                Yes
              </button>
              <button
                className="bg-white border border-violet-300 text-violet-600 py-2 px-8 rounded-md hover:bg-violet-100 transition-all"
                onClick={() => setSignOutPopup(false)}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SidebarItem = ({ icon, label, to, setIsOpen, tooltip }) => (
  <li>
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex items-center px-3 py-2 rounded-md cursor-pointer ${tooltip} ${isActive
          ? "font-semibold bg-violet-100 text-violet-600"
          : "text-gray-600 hover:bg-gray-100 hover:text-violet-600"
        }`
      }
      onClick={() => {
        setTimeout(() => setIsOpen(false), 100);
      }}
    >
      <span className="mr-3">{icon}</span>
      {label}
    </NavLink>
  </li>
);

export default Sidebar;